import { useTranslation } from "react-i18next";
import { PiPlusBold } from "react-icons/pi";
import { Button } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { formatDateToString } from "@utils/dateValidation";
import { truncateWithEllipsis } from "@utils/truncateText";

import { PatientMedicalHistoryItem } from "@customTypes/patient";

import { AddMedicalHistoryModal } from "../AddMedicalHistoryModal";
import { FullMedicalHistoryModal } from "../FullMedicalHistoryModal";

import { StyledMedicalHistoryCard } from "./MedicalHistoryCard.styled";


interface MedicalHistoryCardProps {
  medicalHistory: PatientMedicalHistoryItem[];
}

export const MedicalHistoryCard = ({
  medicalHistory = [],
}: MedicalHistoryCardProps) => {
  const { t } = useTranslation();

  const [fullModalOpened, { open: fullModalOpen, close: fullModalClose }] =
    useDisclosure(false);
  const [addModalOpened, { open: addModalOpen, close: addModalClose }] =
    useDisclosure(false);

  return (
    <StyledMedicalHistoryCard>
      <AddMedicalHistoryModal opened={addModalOpened} close={addModalClose} />
      <div className="card-header">
        <p className="heading">{t("medical-history")}</p>
        <button onClick={addModalOpen} type="button">
          <PiPlusBold className="plus-icon" />
        </button>
      </div>
      <ul className="timeline">
        {medicalHistory.length > 0 ? (
          medicalHistory
            .slice(0, 1)
            .map(({ date, condition, origin, status }, index) => (
              <li className="timeline-item" key={index}>
                <span className="date">
                  {formatDateToString(date).slice(-7)}
                </span>
                <span aria-hidden className="decor"></span>
                <div className="wrapper">
                  <span className="name">
                    {truncateWithEllipsis(condition, 25)}
                  </span>
                  <ul className="tags">
                    <li className="tags-item">
                      <span className={origin === "family" ? "orange" : "blue"}>
                        {t(`${origin}`)}
                      </span>
                    </li>
                    <li className="tags-item">
                      <span className={status === "active" ? "red" : "green"}>
                        {t(`${status}`)}
                      </span>
                    </li>
                  </ul>
                </div>
              </li>
            ))
        ) : (
          <p className="empty-label">{t("empty-medical-history")}</p>
        )}
      </ul>

      <div className="card-footer">
        <FullMedicalHistoryModal
          medicalHistory={medicalHistory}
          opened={fullModalOpened}
          close={fullModalClose}
        />
        <Button
          type="button"
          onClick={fullModalOpen}
          variant="transparent"
          disabled={medicalHistory.length < 1}
          size="compact-sm">
          {t("view-full-history")}
        </Button>
      </div>
    </StyledMedicalHistoryCard>
  );
};
